/**
 * SSRelationships — Relationship Browser & Manager
 * ✅ Refactored to use shared SS component library (components/ss).
 * API: ssRelAPI, ssLookupAPI, ssPartyAPI from ssApi.js
 */
import React, { useState, useEffect, useCallback } from 'react';
import { ssRelAPI, ssLookupAPI, ssPartyAPI } from '../../services/ssApi';
import {
  Page, PageHeader, Card, Modal, Section,
  Button, Badge, Field, Input, Select,
  Spinner, EmptyState, ErrorBanner,
  DataTable, StatBar, SearchBar,
  COLOR, RADIUS, FONT, baseInputStyle,
} from '../../components/ss';

// ── Party search widget (domain-specific — kept as-is) ────────────────────────
function PartyPicker({ label, required, value, onChange }) {
  const [q, setQ]               = useState('');
  const [results, setResults]   = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen]         = useState(false);

  async function search(val) {
    setQ(val);
    if (val.length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const d = await ssPartyAPI.list({ q: val, size: 10 });
      setResults(Array.isArray(d?.data) ? d.data : []);
    } catch { setResults([]); }
    finally { setSearching(false); }
  }

  function pick(p) {
    onChange(p);
    setQ(p.name || `Party #${p.party_id}`);
    setResults([]); setOpen(false);
  }

  return (
    <div style={{ marginBottom: 14, position: 'relative' }}>
      <label style={{ fontSize: FONT.size.sm, fontWeight: FONT.weight.semi, color: COLOR.textSub, display: 'block', marginBottom: 4 }}>
        {label}{required && <span style={{ color: COLOR.red, marginLeft: 2 }}>*</span>}
      </label>
      {value && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <Badge color="blue">ID: {value.party_id}</Badge>
          <span style={{ fontSize: 13, color: COLOR.text }}>{value.name}</span>
          <Button variant="ghost" size="sm" onClick={() => { onChange(null); setQ(''); }}
            style={{ padding: '2px 8px', fontSize: 11 }}>✕</Button>
        </div>
      )}
      {!value && (
        <>
          <input
            style={{ ...baseInputStyle }}
            placeholder="Type to search parties…"
            value={q}
            onChange={e => search(e.target.value)}
            onFocus={() => setOpen(true)}
          />
          {open && results.length > 0 && (
            <div style={{
              position: 'absolute', zIndex: 300, width: '100%',
              background: '#fff', border: `1px solid ${COLOR.grayBorder}`,
              borderRadius: RADIUS.md, boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
              maxHeight: 220, overflowY: 'auto',
            }}>
              {searching && <div style={{ padding: '10px 14px', fontSize: 13, color: COLOR.textMuted }}>Searching…</div>}
              {results.map(p => (
                <div
                  key={p.party_id}
                  onClick={() => pick(p)}
                  style={{ padding: '10px 14px', cursor: 'pointer', fontSize: 13, display: 'flex', gap: 10, alignItems: 'center', borderBottom: `1px solid ${COLOR.graySubtle}` }}
                  onMouseEnter={e => e.currentTarget.style.background = COLOR.grayBg}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <Badge color={p.party_type === 'PER' ? 'blue' : 'purple'}>{p.party_type}</Badge>
                  <span>{p.name}</span>
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: COLOR.grayLight }}>#{p.party_id}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Relationship status badge ──────────────────────────────────────────────────
function RelStatusBadge({ rel }) {
  const now  = new Date();
  const from = rel.from_date ? new Date(rel.from_date) : null;
  const to   = rel.to_date   ? new Date(rel.to_date)   : null;
  if (to && to < now)   return <Badge color="gray">Closed</Badge>;
  if (from && from > now) return <Badge color="amber">Pending</Badge>;
  return <Badge color="green">Active</Badge>;
}

// ── Create Relationship modal ─────────────────────────────────────────────────
function CreateRelModal({ open, onClose, onDone, relTypes }) {
  const [fromParty, setFromParty] = useState(null);
  const [toParty, setToParty]     = useState(null);
  const [typeCode, setTypeCode]   = useState('');
  const [fromDate, setFromDate]   = useState('');
  const [toDate, setToDate]       = useState('');
  const [saving, setSaving]       = useState(false);
  const [err, setErr]             = useState('');

  function reset() {
    setFromParty(null); setToParty(null);
    setTypeCode(''); setFromDate(''); setToDate(''); setErr('');
  }

  async function submit(e) {
    e.preventDefault();
    if (!fromParty || !toParty || !typeCode) { setErr('From party, to party and type are required.'); return; }
    if (fromParty.party_id === toParty.party_id) { setErr('From and To parties must be different.'); return; }
    setSaving(true); setErr('');
    try {
      // Backend CreateRelBody expects type_code (string), not a numeric id.
      await ssRelAPI.create({
        from_party_id: fromParty.party_id,
        to_party_id: toParty.party_id,
        type_code: typeCode,
        from_date: fromDate || null,
        to_date: toDate || null,
      });
      reset(); onDone();
    } catch (ex) { setErr(ex.response?.data?.detail || ex.message); }
    finally { setSaving(false); }
  }

  function handleClose() { reset(); onClose(); }

  return (
    <Modal open={open} title="Create Relationship" onClose={handleClose} fullscreen>
      <div style={{ maxWidth: 860, margin: '0 auto' }}>
      <ErrorBanner>{err}</ErrorBanner>
      <form onSubmit={submit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 16, alignItems: 'start', marginBottom: 14 }}>
          <PartyPicker label="From Party" required value={fromParty} onChange={setFromParty} />
          <div style={{ paddingTop: 28, textAlign: 'center', fontSize: 22, color: COLOR.grayLight }}>→</div>
          <PartyPicker label="To Party" required value={toParty} onChange={setToParty} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 18 }}>
          <Field label="Relationship Type" required>
            <Select value={typeCode} onChange={e => setTypeCode(e.target.value)}>
              <option value="">Select type…</option>
              {relTypes.map(t => (
                <option key={t.relationship_type_id || t.id} value={t.type_code || t.code}>
                  {t.type_code || t.code} — {t.name || t.description || t.type_code || t.code}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="From Date">
            <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} />
          </Field>
          <Field label="To Date (optional)">
            <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} />
          </Field>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={handleClose}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={saving}>{saving ? 'Creating…' : 'Create Relationship'}</Button>
        </div>
      </form>
      </div>
    </Modal>
  );
}

// ── Close Relationship modal ──────────────────────────────────────────────────
function CloseRelModal({ rel, open, onClose, onDone }) {
  const [reason, setReason]   = useState('');
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving]   = useState(false);
  const [err, setErr]         = useState('');

  async function submit(e) {
    e.preventDefault();
    setSaving(true); setErr('');
    // Backend CloseRelBody expects { to_date, reason }; the id field from the
    // API is party_relationship_id (not relationship_id).
    try { await ssRelAPI.close(rel.party_relationship_id, { to_date: endDate, reason }); onDone(); }
    catch (ex) { setErr(ex.response?.data?.detail || ex.message); }
    finally { setSaving(false); }
  }

  return (
    <Modal open={open} title="Close Relationship" onClose={onClose} width={420}>
      <ErrorBanner>{err}</ErrorBanner>
      <p style={{ fontSize: 13, color: COLOR.textMuted, marginTop: 0 }}>
        This will set the end date on the relationship, making it inactive from that date.
      </p>
      <form onSubmit={submit}>
        <Field label="End Date" required style={{ marginBottom: 14 }}>
          <Input required type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
        </Field>
        <Field label="Reason (optional)" style={{ marginBottom: 18 }}>
          <Input placeholder="e.g. Patient transferred" value={reason} onChange={e => setReason(e.target.value)} />
        </Field>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="danger" type="submit" disabled={saving}>{saving ? 'Closing…' : 'Close Relationship'}</Button>
        </div>
      </form>
    </Modal>
  );
}

// ── Table columns ─────────────────────────────────────────────────────────────
const REL_COLS = [
  {
    key: 'from_party_id', label: 'From Party',
    render: (v, row) => (
      <div>
        <span style={{ fontWeight: 600 }}>{row._fromName || row.from_party_name || `Party #${v}`}</span>
        <div style={{ fontSize: 11, color: COLOR.grayLight }}>#{v}</div>
      </div>
    ),
  },
  {
    key: 'type_code', label: 'Type',
    render: v => <Badge color="purple">{v || '—'}</Badge>,
  },
  {
    key: 'to_party_id', label: 'To Party',
    render: (v, row) => (
      <div>
        <span style={{ fontWeight: 600 }}>{row.to_party_name || `Party #${v}`}</span>
        <div style={{ fontSize: 11, color: COLOR.grayLight }}>#{v}</div>
      </div>
    ),
  },
  {
    key: 'from_date', label: 'From',
    render: v => v ? new Date(v).toLocaleDateString() : '—',
  },
  {
    key: 'to_date', label: 'To',
    render: v => v ? new Date(v).toLocaleDateString() : <span style={{ color: COLOR.grayLight }}>Open</span>,
  },
  {
    key: '_status', label: 'Status',
    render: (_, row) => <RelStatusBadge rel={row} />,
  },
];

// ── Main ──────────────────────────────────────────────────────────────────────
export default function SSRelationships() {
  const [relationships, setRelationships] = useState([]);
  const [relTypes, setRelTypes]           = useState([]);
  const [loading, setLoading]             = useState(true);
  const [err, setErr]                     = useState('');
  const [search, setSearch]               = useState('');
  const [filterType, setFilterType]       = useState('');
  const [filterStatus, setFilterStatus]   = useState('');
  const [showCreate, setShowCreate]       = useState(false);
  const [closeTarget, setCloseTarget]     = useState(null);
  const [actionErr, setActionErr]         = useState('');

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([ssLookupAPI.relTypes().catch(() => ({ data: [] }))])
      .then(([rtRes]) => { setRelTypes(rtRes.data || rtRes || []); setErr(''); })
      .catch(ex => setErr(ex.response?.data?.detail || ex.message))
      .finally(() => setLoading(false));
  }, []);

  const searchRelationships = useCallback(async () => {
    if (!search || search.length < 2) return;
    setLoading(true);
    try {
      const parties  = await ssPartyAPI.list({ q: search, size: 5 });
      const pList    = Array.isArray(parties?.data) ? parties.data : [];
      const relResults = [];
      for (const p of pList.slice(0, 3)) {
        // active: false → include closed relationships so the Closed filter
        // and Reopen action actually have rows to work with.
        const rels = await ssPartyAPI.listRelationships(p.party_id, { direction: 'both', active: false });
        const rData = Array.isArray(rels?.data) ? rels.data : (Array.isArray(rels) ? rels : []);
        relResults.push(...rData.map(r => ({ ...r, _fromName: p.name })));
      }
      setRelationships(relResults);
    } catch (ex) {
      setErr(ex.response?.data?.detail || ex.message);
    } finally { setLoading(false); }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!search || search.length < 2) { setRelationships([]); return; }
    const t = setTimeout(searchRelationships, 400);
    return () => clearTimeout(t);
  }, [search, searchRelationships]);

  async function reopen(rel) {
    setActionErr('');
    try { await ssRelAPI.reopen(rel.party_relationship_id); searchRelationships(); }
    catch (ex) { setActionErr(ex.response?.data?.detail || ex.message); }
  }

  const now = new Date();
  const filtered = relationships.filter(r => {
    if (filterType && r.type_code !== filterType) return false;
    const to = r.to_date ? new Date(r.to_date) : null;
    if (filterStatus === 'active'  && to && to < now)  return false;
    if (filterStatus === 'closed'  && !(to && to < now)) return false;
    return true;
  });

  const typeCodesInResults = [...new Set(relationships.map(r => r.type_code).filter(Boolean))];

  const relCols = [
    ...REL_COLS,
    {
      key: '_actions', label: 'Actions',
      render: (_, r) => {
        const isClosed = r.to_date && new Date(r.to_date) < now;
        return isClosed
          ? <Button variant="success" size="sm" onClick={() => reopen(r)}>Reopen</Button>
          : <Button variant="danger"  size="sm" onClick={() => setCloseTarget(r)}>Close</Button>;
      },
    },
  ];

  return (
    <Page>
      <PageHeader
        title="Relationships"
        subtitle="Browse and manage directed party-to-party relationships and care links"
        action={<Button onClick={() => setShowCreate(true)}>+ Create Relationship</Button>}
      />

      <ErrorBanner>{err}</ErrorBanner>
      <ErrorBanner>{actionErr}</ErrorBanner>

      <StatBar style={{ marginBottom: 20 }} stats={[
        { label: 'Relationship Types', value: relTypes.length,   color: COLOR.purple },
        { label: 'Results Shown',      value: filtered.length,   color: COLOR.blue },
        { label: 'Active',             value: filtered.filter(r => !r.to_date || new Date(r.to_date) >= now).length, color: COLOR.greenDark },
        { label: 'Closed',             value: filtered.filter(r => r.to_date && new Date(r.to_date) < now).length,  color: COLOR.redDark },
      ]} />

      {/* Relationship Type reference chips */}
      {relTypes.length > 0 && (
        <Card style={{ marginBottom: 20, padding: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, color: COLOR.text }}>Available Relationship Types</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {relTypes.map(t => (
              <div key={t.relationship_type_id || t.id} style={{ background: COLOR.grayBg, border: `1px solid ${COLOR.grayBorder}`, borderRadius: RADIUS.md, padding: '6px 12px', fontSize: 12 }}>
                <span style={{ fontWeight: 700, fontFamily: 'monospace' }}>{t.type_code || t.code}</span>
                {t.description && <span style={{ color: COLOR.textMuted, marginLeft: 6 }}>— {t.description}</span>}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Search + filter */}
      <Card style={{ marginBottom: 16, padding: 16 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
            <input
              style={{ ...baseInputStyle, paddingLeft: 36 }}
              placeholder="Search by party name to load relationships…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: COLOR.grayLight, fontSize: 14 }}>🔍</span>
          </div>
          {typeCodesInResults.length > 0 && (
            <Select style={{ maxWidth: 200 }} value={filterType} onChange={e => setFilterType(e.target.value)}>
              <option value="">All Types</option>
              {typeCodesInResults.map(tc => <option key={tc} value={tc}>{tc}</option>)}
            </Select>
          )}
          <Select style={{ maxWidth: 160 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="closed">Closed</option>
          </Select>
        </div>
      </Card>

      {/* Results */}
      <Card>
        <DataTable
          columns={relCols}
          rows={filtered}
          rowKey="party_relationship_id"
          loading={loading}
          emptyIcon="🔗"
          emptyTitle={search ? 'No relationships found' : 'Search a party name above'}
          emptyMsg={search ? 'Try a different name or create a new relationship.' : 'Type at least 2 characters to load relationships.'}
          minWidth={700}
        />
      </Card>

      <CreateRelModal
        open={showCreate}
        relTypes={relTypes}
        onClose={() => setShowCreate(false)}
        onDone={() => { setShowCreate(false); searchRelationships(); }}
      />
      <CloseRelModal
        rel={closeTarget}
        open={!!closeTarget}
        onClose={() => setCloseTarget(null)}
        onDone={() => { setCloseTarget(null); searchRelationships(); }}
      />
    </Page>
  );
}
